import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { PlayCircle, CheckCircle2, MoonStar, Users, Ticket, UserRound, Heart } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import VideoEvidenceModal from "@/components/VideoEvidenceModal";
import SeatHistoryModal from "@/components/SeatHistoryModal";
import ServiceTimeline from "@/components/ServiceTimeline";

function fmtTime(iso) {
  return new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
}

// Quick popover showing the line items on a matched POS ticket.
function TicketPeek({ grp, ev, money }) {
  const ticketId = ev.pos_transaction_id;
  const total = ev.ticket_total ?? ev.expected_price;
  const items = grp.length > 1 ? grp : [ev];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`pos-peek-${ev.id}`}
          onClick={(e) => e.stopPropagation()}
          className="font-mono text-xs text-slate-300 hover:text-[#38BDF8] underline decoration-dotted underline-offset-2 transition-colors duration-150"
        >
          {ticketId}
        </button>
      </PopoverTrigger>
      <PopoverContent
        onClick={(e) => e.stopPropagation()}
        className="w-72 bg-[#1E2638] border-[#2A3550] text-slate-200 p-0 overflow-hidden"
        data-testid={`pos-peek-content-${ev.id}`}
      >
        <div className="px-4 py-3 border-b border-[#2A3550] flex items-center gap-2">
          <Ticket className="h-4 w-4 text-emerald-400" />
          <span className="font-mono text-sm">{ticketId}</span>
        </div>
        {(ev.pos_time || ev.pos_cashier) && (
          <div className="px-4 py-2 border-b border-[#2A3550] text-xs text-slate-400 flex items-center justify-between gap-2" data-testid={`pos-peek-meta-${ev.id}`}>
            <span>{ev.pos_time ? `Rung up ${fmtTime(ev.pos_time)}` : ""}</span>
            {ev.pos_cashier && <span className="inline-flex items-center gap-1.5"><UserRound className="h-3 w-3" /> {ev.pos_cashier}</span>}
          </div>
        )}
        <div className="px-4 py-3 space-y-2 max-h-56 overflow-y-auto">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between text-sm">
              <div className="text-slate-300">
                {it.chair_label} <span className="text-slate-500 capitalize">· {it.classification}</span>
              </div>
              <div className={it.pos_transaction_id ? "text-slate-200" : "text-red-400"}>
                {money(it.expected_price)}{!it.pos_transaction_id && " · unmatched"}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-[#2A3550] flex items-center justify-between text-sm">
          <span className="text-slate-400">Ticket total</span>
          <span className="font-medium">{money(total)}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Reconciliation() {
  const { money, branding } = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get("filter");
  const [filter, setFilter] = useState(["all", "flagged", "matched"].includes(urlFilter) ? urlFilter : "all");
  const [selected, setSelected] = useState(null);
  const [seat, setSeat] = useState(null); // { storeId, chair } → opens SeatHistoryModal
  const [timelineEv, setTimelineEv] = useState(null);
  const deepEvent = searchParams.get("event");
  useEffect(() => {
    if (!deepEvent) return;
    api.get(`/events/${deepEvent}`).then((r) => setTimelineEv(r.data)).catch(() => {});
  }, [deepEvent]);
  const qc = useQueryClient();
  const [closing, setClosing] = useState(false);

  const closeDay = async () => {
    setClosing(true);
    try {
      const { data } = await api.post("/eod/close", {});
      if (data.escalated > 0)
        toast.success(`Day closed — ${data.escalated} suspicious service${data.escalated > 1 ? "s" : ""} escalated to the owner by email + inbox`);
      else
        toast.info("Day closed — no suspicious services to escalate");
      qc.invalidateQueries();
    } catch { toast.error("Could not close out the day"); }
    setClosing(false);
  };

  const flaggedParam = filter === "flagged" ? true : filter === "matched" ? false : undefined;
  const { data: events } = useQuery({
    queryKey: ["recon-events", filter],
    queryFn: () =>
      api.get("/events", { params: { limit: 300, ...(flaggedParam !== undefined ? { flagged: flaggedParam } : {}) } })
        .then((r) => r.data),
  });

  const openSeat = (ev) => setSeat({ storeId: ev.store_id, chair: { id: ev.chair_id, label: ev.chair_label } });
  const openStore = (ev, e) => { e.stopPropagation(); navigate(`/floor?store=${ev.store_number}&from=reconciliation`); };

  // Group seats that share a single POS ticket (party of 2-4 paying together)
  const groups = useMemo(() => {
    const map = new Map();
    (events || []).forEach((ev) => {
      const key = ev.party_id || ev.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ev);
    });
    return Array.from(map.values());
  }, [events]);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Seat-to-Sale Reconciliation</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
            Every service, matched to a sale
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={closeDay}
            disabled={closing}
            data-testid="close-day-btn"
            className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium border border-[#2A3550] bg-[#1E2638] hover:border-[#38BDF8] text-slate-100 transition-colors duration-200 disabled:opacity-60"
          >
            <MoonStar className="h-4 w-4" /> {closing ? "Closing…" : "Close out the day"}
          </button>
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-[#1E2638] border border-[#2A3550]">
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="flagged" data-testid="filter-flagged">Flagged</TabsTrigger>
              <TabsTrigger value="matched" data-testid="filter-matched">Matched</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="mt-8 surface-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="reconciliation-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-400 border-b border-[#2A3550]">
                <th className="px-5 py-4 font-semibold">Store</th>
                <th className="px-5 py-4 font-semibold">Chair</th>
                <th className="px-5 py-4 font-semibold">Time</th>
                <th className="px-5 py-4 font-semibold">Duration</th>
                <th className="px-5 py-4 font-semibold">Type</th>
                <th className="px-5 py-4 font-semibold">Expected</th>
                <th className="px-5 py-4 font-semibold">POS Sale</th>
                <th className="px-5 py-4 font-semibold">Status</th>
                <th className="px-5 py-4 font-semibold text-right"><span className="inline-block text-left">Video protocol</span></th>
              </tr>
            </thead>
            <tbody>
              {groups.map((grp) => {
                const isParty = grp.length > 1;
                const ticket = grp.find((g) => g.party_ticket)?.party_ticket;
                const ticketTotal = grp[0]?.ticket_total;
                const paidSeats = grp[0]?.ticket_seats;
                return (
                  <React.Fragment key={grp[0].party_id || grp[0].id}>
                    {isParty && (
                      <tr className="bg-[#171E2E]" data-testid={`party-header-${grp[0].party_id}`}>
                        <td colSpan={9} className="px-5 py-2.5">
                          <div className="flex items-center gap-2 text-xs text-slate-300">
                            <Users className="h-3.5 w-3.5 text-[#38BDF8]" />
                            <span className="font-medium">Party of {grp.length}</span>
                            <span className="text-slate-500">·</span>
                            {ticket ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Ticket className="h-3.5 w-3.5 text-emerald-400" />
                                <span className="font-mono">{ticket}</span>
                                <span className="text-slate-400">
                                  covered {paidSeats} of {grp.length} chairs · {money(ticketTotal)}
                                </span>
                              </span>
                            ) : (
                              <span className="text-red-400">No POS ticket — whole party unmatched</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    {grp.map((ev) => (
                      <tr key={ev.id} data-testid={`recon-row-${ev.id}`}
                          onClick={() => setTimelineEv(ev)}
                          title="View this service's timeline — seat to POS"
                          className={`border-b border-[#2A3550] hover:bg-[#232C40] transition-colors duration-150 cursor-pointer ${
                            isParty ? "border-l-2 border-l-[#38BDF8]/40" : ""}`}>
                        <td className="px-5 py-3.5 font-medium">
                          <button
                            onClick={(e) => openStore(ev, e)}
                            data-testid={`recon-store-${ev.id}`}
                            title="Open this store's live floor"
                            className="hover:text-[#38BDF8] transition-colors duration-150"
                          >
                            #{ev.store_number}
                          </button>
                        </td>
                        <td className="px-5 py-3.5 text-slate-400">{ev.chair_label}</td>
                        <td className="px-5 py-3.5 text-slate-400">{fmtTime(ev.started_at)}</td>
                        <td className="px-5 py-3.5 text-slate-400">{ev.duration_min} min</td>
                        <td className="px-5 py-3.5 text-slate-400 capitalize">{ev.classification}</td>
                        <td className="px-5 py-3.5">{money(ev.expected_price)}</td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-400">
                          {ev.kind === "charitable" ? (
                            <span className="inline-flex items-center gap-1.5 text-rose-300 not-italic font-sans">
                              <Heart className="h-3.5 w-3.5" /> Charitable
                            </span>
                          ) : ev.pos_transaction_id ? (
                            <span className="inline-flex items-center gap-1.5">
                              <TicketPeek grp={grp} ev={ev} money={money} />
                              {isParty && (
                                <span className="rounded bg-[#232C40] px-1.5 py-0.5 text-[10px] text-slate-300">
                                  {ev.party_index}/{ev.party_size}
                                </span>
                              )}
                            </span>
                          ) : isParty && ev.party_ticket ? (
                            <span className="text-[11px] text-red-400/90 not-italic">not on {ev.party_ticket}</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-5 py-3.5">
                          {ev.kind === "charitable" ? (
                            <span className="inline-flex items-center gap-1.5 text-rose-300">
                              <Heart className="h-3.5 w-3.5" /> Donated{ev.beneficiary_name ? ` · ${ev.beneficiary_name}` : ""}
                            </span>
                          ) : ev.flagged ? (
                            <span className="inline-flex items-center gap-1.5 text-red-400">
                              <span className="h-2 w-2 rounded-full bg-red-500/100" /> Unmatched
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-emerald-400">
                              <CheckCircle2 className="h-3.5 w-3.5" /> Matched
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          {ev.kind === "charitable" ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelected(ev); }}
                              data-testid={`view-proof-${ev.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-white bg-rose-500/80 hover:bg-rose-500 transition-colors duration-200"
                            >
                              <PlayCircle className="h-3.5 w-3.5" /> Viewing Protocol
                            </button>
                          ) : ev.flagged ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelected(ev); }}
                              data-testid={`view-proof-${ev.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg text-white transition-opacity duration-200 hover:opacity-90"
                              style={{ backgroundColor: branding.primary_color }}
                            >
                              <PlayCircle className="h-3.5 w-3.5" /> Viewing Protocol
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">discarded</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {events && events.length === 0 && (
          <div className="py-12 text-center text-sm text-slate-400">No events for this filter.</div>
        )}
      </div>

      <VideoEvidenceModal event={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} />
      <SeatHistoryModal storeId={seat?.storeId} chair={seat?.chair} open={!!seat} onOpenChange={(v) => !v && setSeat(null)} />
      <ServiceTimeline event={timelineEv} open={!!timelineEv}
        onOpenChange={(v) => !v && setTimelineEv(null)}
        onWatch={(e) => { setTimelineEv(null); setSelected(e); }} />
    </div>
  );
}
